/**
 * PowerOfAttorney — PF v2 native configuration UI (Wave 1, module 1b).
 *
 * Lifts the presentational markup from the legacy `PowerOfAttorneyStepController`
 * (`PurchaseFlow/steps/PowerOfAttorneyStep`) but rewrites ALL data wiring onto the
 * PF v2 store + file hub via `usePoaConfigController` — no constate provider, no
 * 3× `useFileAPI`, no imperative handle, no legacy controller. F-B1 dies by
 * construction on the upload axis via the interaction-gated projection→DTO sync
 * (§4b): state lives in `productConfigs`, uploads go through the file hub, and the
 * DTO is only written after the first user interaction this session.
 *
 * Dropped vs. the legacy controller:
 *   - nothing on the analytics axis any more: the document configurator that
 *     hosts this page announces it for `ENTER_ORDER_POA_PAGE_SCREEN`.
 *   - `StepWrapper` (a `PurchaseFlow/*` import) → replaced with an in-component
 *     title/subtitle header.
 *
 * ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate). Reuses the
 * `power_of_attorney` + `purchase_flow:power_of_attorney_step` i18n namespaces
 * verbatim (no re-translation for Wave 1).
 */

import React from 'react'
import { Fade, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { FileType } from 'constants/misc'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { PowerOfAttorneyUpload } from 'components/common/PowerOfAttorneyUpload'
import { SystemMessage } from 'components/common/SystemMessage'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { CartMutationHandler } from '../../adapters/bridge'

import { usePoaConfigController } from './usePoaFilenameSync'

const powerOfAttorneySample = `${process.env.PUBLIC_URL}/assets/power-of-attorney-sample.pdf`

/** 5 MB cap — legacy parity (`PowerOfAttorneyStepController.onCheckFileSize`). */
const onCheckFileSize = (fileSize: number): boolean => fileSize > 0 && fileSize <= 5 * 1024 * 1024

export interface PowerOfAttorneyProps {
  cartItemId: CartItemId
  /** Accepted for registry signature uniformity — PoA emits no cart mutations. */
  onCartMutation?: CartMutationHandler
}

/**
 * @component PowerOfAttorney
 * Native power-of-attorney configuration form. Reads/writes `productConfigs` via
 * `usePoaConfigController`; uploads go through the PF v2 file hub.
 */
export const PowerOfAttorney: React.FC<PowerOfAttorneyProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['power_of_attorney', 'purchase_flow', 'purchase_flow_revamp'])

  const {
    requiresRepresentative,
    hasRestoredFiles,
    main,
    reps,
    other,
    uploadMain,
    deleteMain,
    uploadReps,
    deleteReps,
    uploadOther,
    deleteOther,
  } = usePoaConfigController(cartItemId)

  return (
    <Fade in>
      <Stack direction="column" gap={2} alignItems="stretch" justifyContent="flex-start">

        <Stack gap={0.4}>

          <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>
            {t('purchase_flow:power_of_attorney_step.section_title')}
          </Typography>

          <Typography variant="text-sm" color={GRAY_700}>
            {t('purchase_flow:power_of_attorney_step.section_description')}
          </Typography>

        </Stack>

        {hasRestoredFiles &&
          <SystemMessage
            variant="warning"
            message={t('purchase_flow_revamp:configuration.power_of_attorney.session_orphan_warning')}
          />
        }

        {!requiresRepresentative &&
          <PowerOfAttorneyUpload
            variant="owners"
            boxStyle="elevated"
            sampleUrl={powerOfAttorneySample}
            uploadSlot={
              <FileAPIUpload
                title={t('power_of_attorney')}
                files={main.entries}
                maxNumberOfFiles={1}
                isDraggingFile={false}
                acceptedFileTypes={[FileType.PDF]}
                fileSizeCheckFailedWarning={t('purchase_flow:power_of_attorney_step.file_size_warning')}
                onCheckFileSize={onCheckFileSize}
                uploadHandler={(acceptedFiles) => uploadMain(acceptedFiles)}
                onDelete={(fileId) => deleteMain(fileId)}
                onCancelUpload={(fileId) => deleteMain(fileId)}
              />
            }
          />
        }

        {requiresRepresentative && <>

          <PowerOfAttorneyUpload
            variant="owner_from_owner"
            boxStyle="elevated"
            uploadContentTitle={t('upload_content_title_general_info')}
            uploadSlot={
              <FileAPIUpload
                title={t('power_of_attorney_from_owner_to_owner')}
                files={main.entries}
                maxNumberOfFiles={1}
                isDraggingFile={false}
                acceptedFileTypes={[FileType.PDF]}
                fileSizeCheckFailedWarning={t('purchase_flow:power_of_attorney_step.file_size_warning')}
                onCheckFileSize={onCheckFileSize}
                uploadHandler={(acceptedFiles) => uploadMain(acceptedFiles)}
                onDelete={(fileId) => deleteMain(fileId)}
                onCancelUpload={(fileId) => deleteMain(fileId)}
              />
            }
          />

          <PowerOfAttorneyUpload
            variant="representatives"
            boxStyle="elevated"
            sampleUrl={powerOfAttorneySample}
            uploadSlot={
              <FileAPIUpload
                title={t('power_of_attorney_reps')}
                files={reps.entries}
                maxNumberOfFiles={1}
                isDraggingFile={false}
                acceptedFileTypes={[FileType.PDF]}
                fileSizeCheckFailedWarning={t('purchase_flow:power_of_attorney_step.file_size_warning')}
                onCheckFileSize={onCheckFileSize}
                uploadHandler={(acceptedFiles) => uploadReps(acceptedFiles)}
                onDelete={(fileId) => deleteReps(fileId)}
                onCancelUpload={(fileId) => deleteReps(fileId)}
              />
            }
          />

        </>}

        {/* Supplementary "other" files (0..15) */}
        <FileAPIUpload
          title={t('other_files_upload')}
          files={other.entries}
          acceptedFileTypes={[FileType.PDF]}
          isDraggingFile={false}
          fileSizeCheckFailedWarning={t('purchase_flow:power_of_attorney_step.file_size_warning')}
          onCheckFileSize={onCheckFileSize}
          uploadHandler={(acceptedFiles) => uploadOther(acceptedFiles)}
          onDelete={(fileId) => deleteOther(fileId)}
          onCancelUpload={(fileId) => deleteOther(fileId)}
          maxNumberOfFiles={15}
        />

      </Stack>
    </Fade>
  )
}
